import React, { useState, createElement } from 'react';
import { DownloadIcon, FilterIcon, CheckSquareIcon } from 'lucide-react';
import Card from '../../components/ui/Card';
import Table from '../../components/ui/Table';
import StatusBadge from '../../components/ui/StatusBadge';
const SCHEME_LOGOS: Record<string, string> = {
  Visa: 'https://cdn.magicpatterns.com/uploads/8xq2uxNNNydtTwxH2KaA3P/Visa_Logo.png',
  Mastercard: 'https://cdn.magicpatterns.com/uploads/uhhVfXBtdJ27miMoBQB5UQ/MasterCard_Logo.png',
  UnionPay: 'https://cdn.magicpatterns.com/uploads/f6k19YT6hK7xuaUtj73TGV/UnionPay_Logo.png',
  PayPak: 'https://cdn.magicpatterns.com/uploads/5aESH9zRKbVPLTL9anhfA2/PayPak_Logo.png'
};
const SchemeResponseExport: React.FC = () => {
  const [scheme, setScheme] = useState<string>('Visa');
  const [selectedRecords, setSelectedRecords] = useState<string[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  // Mock Data: Disputes ready for scheme response
  const readyDisputes = [{
    id: 'DSP-2023-081',
    schemeCaseId: 'V-998211',
    scheme: 'Visa',
    merchant: 'TechStore PK',
    type: 'Representment',
    amount: 45000,
    date: '2023-10-26',
    status: 'Ready for Export',
    arn: '741234567890123456789012',
    txId: 'TXN-99281',
    merchantId: 'MER-55421',
    reasonCode: '4837',
    evidenceRef: 'DOC-12345'
  }, {
    id: 'DSP-2023-082',
    schemeCaseId: 'V-998215',
    scheme: 'Visa',
    merchant: 'Fashion Hub',
    type: 'Accept Chargeback',
    amount: 12500,
    date: '2023-10-26',
    status: 'Ready for Export',
    arn: '741234567890123456789013',
    txId: 'TXN-99282',
    merchantId: 'MER-55422',
    reasonCode: '4853',
    evidenceRef: ''
  }, {
    id: 'DSP-2023-085',
    schemeCaseId: 'V-998220',
    scheme: 'Visa',
    merchant: 'Grocery Plus',
    type: 'Provide Evidence',
    amount: 3400,
    date: '2023-10-25',
    status: 'Ready for Export',
    arn: '741234567890123456789014',
    txId: 'TXN-99283',
    merchantId: 'MER-55423',
    reasonCode: '4855',
    evidenceRef: 'DOC-12346'
  }, {
    id: 'DSP-2023-088',
    schemeCaseId: 'M-554122',
    scheme: 'Mastercard',
    merchant: 'Electro World',
    type: 'Representment',
    amount: 89000,
    date: '2023-10-25',
    status: 'Ready for Export',
    arn: '741234567890123456789015',
    txId: 'TXN-99284',
    merchantId: 'MER-55424',
    reasonCode: '4837',
    evidenceRef: 'DOC-12347'
  }, {
    id: 'DSP-2023-091',
    schemeCaseId: 'U-112099',
    scheme: 'UnionPay',
    merchant: 'Travel Agency',
    type: 'Accept Chargeback',
    amount: 150000,
    date: '2023-10-24',
    status: 'Ready for Export',
    arn: '741234567890123456789016',
    txId: 'TXN-99285',
    merchantId: 'MER-55425',
    reasonCode: '4834',
    evidenceRef: ''
  }, {
    id: 'DSP-2023-095',
    schemeCaseId: 'PP-882101',
    scheme: 'PayPak',
    merchant: 'Karachi Electronics',
    type: 'Representment',
    amount: 25000,
    date: '2023-10-27',
    status: 'Ready for Export',
    arn: '741234567890123456789017',
    txId: 'TXN-99286',
    merchantId: 'MER-55426',
    reasonCode: '4837',
    evidenceRef: 'DOC-12348'
  }, {
    id: 'DSP-2023-096',
    schemeCaseId: 'PP-882102',
    scheme: 'PayPak',
    merchant: 'Lahore Textiles',
    type: 'Provide Evidence',
    amount: 18500,
    date: '2023-10-27',
    status: 'Ready for Export',
    arn: '741234567890123456789018',
    txId: 'TXN-99287',
    merchantId: 'MER-55427',
    reasonCode: '4853',
    evidenceRef: 'DOC-12349'
  }];
  const filteredDisputes = readyDisputes.filter(d => d.scheme === scheme);
  const toggleSelection = (id: string) => {
    if (selectedRecords.includes(id)) {
      setSelectedRecords(selectedRecords.filter(r => r !== id));
    } else {
      setSelectedRecords([...selectedRecords, id]);
    }
  };
  const toggleAll = () => {
    if (selectedRecords.length === filteredDisputes.length) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(filteredDisputes.map(d => d.id));
    }
  };
  const columns = [{
    header: <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" checked={selectedRecords.length === filteredDisputes.length && filteredDisputes.length > 0} onChange={toggleAll} data-id="element-4190" />,
    accessor: (row: any) => <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" checked={selectedRecords.includes(row.id)} onChange={() => toggleSelection(row.id)} data-id="element-4191" />,
    className: 'w-10'
  }, {
    header: 'Internal ID',
    accessor: 'id',
    className: 'font-medium text-gray-900 dark:text-white'
  }, {
    header: 'Scheme Case ID',
    accessor: 'schemeCaseId',
    className: 'font-mono text-xs'
  }, {
    header: 'Merchant',
    accessor: 'merchant'
  }, {
    header: 'Response Type',
    accessor: (row: any) => {
      let badgeType: any = 'default';
      if (row.type === 'Representment') badgeType = 'info';
      if (row.type === 'Accept Chargeback') badgeType = 'warning';
      if (row.type === 'Provide Evidence') badgeType = 'success';
      return <StatusBadge status={badgeType} text={row.type} data-id="element-4192" />;
    }
  }, {
    header: 'Amount',
    accessor: (row: any) => `PKR ${row.amount.toLocaleString()}`
  }, {
    header: 'Decision Date',
    accessor: 'date'
  }];
  const generateFileContent = (records: any[], scheme: string) => {
    const dateStr = new Date().toISOString().split('T')[0].replace(/-/g, '');
    let content = '';
    let filename = '';
    let mimeType = '';
    if (scheme === 'Mastercard') {
      content = `<?xml version="1.0" encoding="UTF-8"?>\n<MastercomResponse>\n`;
      records.forEach(r => {
        content += `  <DisputeResponse>\n`;
        content += `    <Case_ID>${r.schemeCaseId}</Case_ID>\n`;
        content += `    <ARN>${r.arn}</ARN>\n`;
        content += `    <Transaction_ID>${r.txId}</Transaction_ID>\n`;
        content += `    <Merchant_ID>${r.merchantId}</Merchant_ID>\n`;
        content += `    <Dispute_Amount>${r.amount}</Dispute_Amount>\n`;
        content += `    <Response_Type>${r.type}</Response_Type>\n`;
        content += `    <Reason_Code>${r.reasonCode}</Reason_Code>\n`;
        content += `    <Evidence_Ref>${r.evidenceRef}</Evidence_Ref>\n`;
        content += `    <Response_Date>${r.date}</Response_Date>\n`;
        content += `  </DisputeResponse>\n`;
      });
      content += `</MastercomResponse>`;
      filename = `mastercard_response_${dateStr}.xml`;
      mimeType = 'application/xml';
    } else {
      // CSV format for Visa, UnionPay, PayPak
      content = 'Case_ID,ARN,Transaction_ID,Merchant_ID,Dispute_Amount,Response_Type,Reason_Code,Evidence_Ref,Response_Date\n';
      records.forEach(r => {
        content += `${r.schemeCaseId},${r.arn},${r.txId},${r.merchantId},${r.amount},${r.type},${r.reasonCode},${r.evidenceRef},${r.date}\n`;
      });
      filename = `${scheme.toLowerCase()}_response_${dateStr}.csv`;
      mimeType = 'text/csv';
    }
    return {
      content,
      filename,
      mimeType
    };
  };
  const handleGenerate = () => {
    if (selectedRecords.length === 0) return;
    setIsGenerating(true);
    setTimeout(() => {
      const recordsToExport = readyDisputes.filter(d => selectedRecords.includes(d.id));
      const {
        content,
        filename,
        mimeType
      } = generateFileContent(recordsToExport, scheme);
      const blob = new Blob([content], {
        type: mimeType
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setIsGenerating(false);
      setSelectedRecords([]);
    }, 1500);
  };
  return <div className="space-y-6" data-id="element-4193">
      <div data-id="element-4194">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-4195">
          Scheme Response Export
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-4196">
          Generate scheme-compliant response files (Representments, Acceptances,
          Evidence) to upload to scheme portals (Visa, Mastercard, UnionPay,
          PayPak).
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-id="element-4197">
        <div className="lg:col-span-1 space-y-4" data-id="element-4198">
          <Card title="Select Scheme" data-id="element-4199">
            <div className="space-y-2" data-id="element-4200">
              {['Visa', 'Mastercard', 'UnionPay', 'PayPak'].map(s => <button key={s} onClick={() => {
              setScheme(s);
              setSelectedRecords([]);
            }} className={`w-full flex items-center justify-between p-3 rounded-lg border transition-all ${scheme === s ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 font-medium' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'}`} data-id="element-4201">
                  <div className="flex items-center" data-id="element-4202">
                    <img src={SCHEME_LOGOS[s]} alt={s} className="h-5 object-contain mr-3" data-id="element-4203" />
                    {s}
                  </div>
                  <span className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 py-0.5 px-2 rounded-full text-xs" data-id="element-4204">
                    {readyDisputes.filter(d => d.scheme === s).length}
                  </span>
                </button>)}
            </div>
          </Card>

          <Card className="bg-blue-50/50 dark:bg-blue-900/10 border-blue-100 dark:border-blue-800" data-id="element-4205">
            <h3 className="text-sm font-semibold text-blue-800 dark:text-blue-300 mb-2" data-id="element-4206">
              Export Summary
            </h3>
            <div className="space-y-3 text-sm" data-id="element-4207">
              <div className="flex justify-between text-gray-600 dark:text-gray-400" data-id="element-4208">
                <span data-id="element-4209">Selected Scheme:</span>
                <span className="font-medium text-gray-900 dark:text-white" data-id="element-4210">
                  {scheme}
                </span>
              </div>
              <div className="flex justify-between text-gray-600 dark:text-gray-400" data-id="element-4211">
                <span data-id="element-4212">Records Selected:</span>
                <span className="font-medium text-gray-900 dark:text-white" data-id="element-4213">
                  {selectedRecords.length}
                </span>
              </div>
              <div className="flex justify-between text-gray-600 dark:text-gray-400" data-id="element-4214">
                <span data-id="element-4215">Target Format:</span>
                <span className="font-medium text-gray-900 dark:text-white" data-id="element-4216">
                  {scheme === 'Mastercard' ? 'XML (Mastercom)' : 'CSV'}
                </span>
              </div>
            </div>
            <button onClick={handleGenerate} disabled={selectedRecords.length === 0 || isGenerating} className="mt-6 w-full flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-4217">
              {isGenerating ? <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" data-id="element-4218"></div>
                  Generating...
                </> : <>
                  <DownloadIcon className="h-4 w-4 mr-2" data-id="element-4219" />
                  Generate {scheme} File
                </>}
            </button>
          </Card>
        </div>

        <div className="lg:col-span-3" data-id="element-4220">
          <Card data-id="element-4221">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-6" data-id="element-4222">
              <h2 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-4223">
                Pending Responses for {scheme}
              </h2>
              <div className="flex items-center space-x-2" data-id="element-4224">
                <FilterIcon className="h-5 w-5 text-gray-400" data-id="element-4225" />
                <select className="block w-full pl-3 pr-10 py-2 text-sm border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md dark:bg-gray-800 dark:text-white" data-id="element-4226">
                  <option data-id="element-4227">All Response Types</option>
                  <option data-id="element-4228">Representment</option>
                  <option data-id="element-4229">Accept Chargeback</option>
                  <option data-id="element-4230">Provide Evidence</option>
                </select>
              </div>
            </div>

            {filteredDisputes.length > 0 ? <Table columns={columns} data={filteredDisputes} keyField="id" pagination={false} data-id="element-4231" /> : <div className="text-center py-12" data-id="element-4232">
                <CheckSquareIcon className="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600" data-id="element-4233" />
                <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white" data-id="element-4234">
                  All caught up!
                </h3>
                <p className="mt-1 text-sm text-gray-500" data-id="element-4235">
                  There are no pending responses ready for {scheme} export.
                </p>
              </div>}
          </Card>
        </div>
      </div>
    </div>;
};
export default SchemeResponseExport;